import { Link } from "react-router-dom";
import { CheckCircle2, ChevronLeft, MessageCircle, Phone, Truck } from "lucide-react";
import SEO, { breadcrumbLd, faqLd, serviceLd } from "@/components/SEO";
import Faq from "@/components/Faq";
import { Reveal } from "@/components/Reveal";
import { LocationWhatsAppButton } from "@/components/WhatsAppCTA";
import { CtaBand } from "@/pages/Home";
import { BUSINESS, waLink } from "@/data/business";
import { SERVICES } from "@/data/services";
import { AREAS } from "@/data/areas";

export default function ServicePage({ service }) {
  const crumb = [
    { name: "الرئيسية", path: "/" },
    { name: "خدماتنا", path: "/#services" },
    { name: service.name, path: `/${service.slug}` },
  ];
  const others = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 4);

  return (
    <>
      <SEO
        title={service.title}
        description={service.description}
        path={service.slug}
        image={service.image}
        jsonLd={[serviceLd(service), faqLd(service.faqs), breadcrumbLd(crumb)]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden grain pt-28 pb-16">
        <div className="absolute inset-0">
          <img src={service.image} alt={service.alt} fetchPriority="high" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/60" />
          <div className="absolute inset-0 spotlight" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <nav aria-label="مسار التنقل" className="text-xs text-gray-400 mb-5 flex items-center gap-1.5" data-testid="breadcrumb">
            <Link to="/" className="hover:text-gold-soft">الرئيسية</Link>
            <ChevronLeft size={12} />
            <Link to="/#services" className="hover:text-gold-soft">خدماتنا</Link>
            <ChevronLeft size={12} />
            <span className="text-gold-soft">{service.name}</span>
          </nav>
          <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl" data-testid="service-h1">
            {service.h1}
          </h1>
          <p className="mt-6 text-lg text-gray-300 leading-relaxed max-w-2xl">{service.intro[0]}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={waLink(`مرحباً، أرغب في طلب خدمة: ${service.name} في الرياض.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="service-order-cta"
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-ink font-black rounded-full px-7 py-3.5 transition-colors"
            >
              <MessageCircle size={18} /> اطلب هذه الخدمة عبر واتساب
            </a>
            <LocationWhatsAppButton
              testid="service-location-cta"
              label="أرسل موقعك للفني"
              className="inline-flex items-center gap-2 border border-gold/50 text-gold-soft hover:bg-gold hover:text-ink font-bold rounded-full px-7 py-3.5 transition-colors"
            />
            <a
              href={BUSINESS.telLink}
              data-testid="service-call-cta"
              className="inline-flex items-center gap-2 border border-blood/60 text-white hover:bg-blood font-bold rounded-full px-7 py-3.5 transition-colors"
            >
              <Phone size={18} /> <span dir="ltr">{BUSINESS.phoneDisplay}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Features strip */}
      <section className="border-y border-edge bg-panel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {service.features.map((f, i) => (
            <div key={i} className="flex items-center gap-2.5 text-sm text-gray-300">
              <CheckCircle2 size={17} className="text-gold shrink-0" />
              {f}
            </div>
          ))}
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-20">
        {service.intro[1] && (
          <Reveal>
            <p className="text-lg text-gray-300 leading-loose mb-12">{service.intro[1]}</p>
          </Reveal>
        )}
        <div className="space-y-14">
          {service.sections.map((sec, i) => (
            <Reveal key={sec.h2} delay={0.05}>
              <article>
                <h2 className="font-display font-black text-2xl sm:text-3xl text-white mb-5 flex items-center gap-3">
                  <span className="font-mono text-gold text-lg">0{i + 1}</span>
                  {sec.h2}
                </h2>
                <p className="text-gray-300 leading-loose mb-4">{sec.body}</p>
                {sec.list && (
                  <ul className="grid sm:grid-cols-2 gap-3 mt-5">
                    {sec.list.map((li, j) => (
                      <li key={j} className="flex items-start gap-2.5 bg-panel border border-edge rounded-xl px-4 py-3 text-sm text-gray-300 leading-relaxed">
                        <CheckCircle2 size={16} className="text-gold shrink-0 mt-0.5" />
                        {li}
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            </Reveal>
          ))}
        </div>

        {/* Steps */}
        <Reveal className="mt-16">
          <h2 className="font-display font-black text-2xl sm:text-3xl text-white mb-8">كيف تطلب الخدمة؟</h2>
          <ol className="grid sm:grid-cols-4 gap-4">
            {service.steps.map((st, i) => (
              <li key={i} className="bg-panel border border-edge rounded-2xl p-5">
                <span className="font-mono text-gold font-bold text-lg">0{i + 1}</span>
                <p className="text-sm text-gray-300 mt-2 leading-relaxed">{st}</p>
              </li>
            ))}
          </ol>
        </Reveal>

        {/* FAQ */}
        <Reveal className="mt-16">
          <h2 className="font-display font-black text-2xl sm:text-3xl text-white mb-8">أسئلة شائعة</h2>
          <Faq items={service.faqs} testidPrefix="service-faq" />
        </Reveal>
      </section>

      {/* Internal links */}
      <section className="bg-panel border-t border-edge py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-10">
          <div>
            <h3 className="font-display font-bold text-white mb-5">خدمات أخرى قد تحتاجها</h3>
            <div className="flex flex-wrap gap-2.5">
              {others.map((s) => (
                <Link key={s.slug} to={`/${s.slug}`} data-testid={`service-related-${s.slug}`} className="text-sm border border-edge hover:border-gold/60 hover:text-gold-soft text-gray-300 rounded-full px-4 py-2 transition-colors">
                  {s.name}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-display font-bold text-white mb-5">نخدم الرياض كاملة</h3>
            <div className="flex flex-wrap gap-2.5">
              {AREAS.map((a) => (
                <Link key={a.slug} to={`/${a.slug}`} data-testid={`service-area-${a.slug}`} className="text-sm border border-edge hover:border-gold/60 hover:text-gold-soft text-gray-300 rounded-full px-4 py-2 transition-colors">
                  بنشر متنقل {a.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaBand title="اطلب فنياً الآن — نصل إليك أينما كنت" testid="service-cta-band" />
    </>
  );
}
